<template>
  <div
    class="modal"
    role="dialog"
    aria-modal="true"
    aria-labelledby="item-details-title"
  >
    <div class="popup-content">

      <!-- ==========================================
           MODAL HEADER
      =========================================== -->
      <div class="popup-header">
        <div class="header-content">
          <h3 id="item-details-title">
            Item Details
          </h3>

          <p class="header-description">
            Product information and district pricing
          </p>
        </div>

        <button
          type="button"
          @click="closeModal"
          class="btn btn-light close-btn"
          aria-label="Close item details"
        >
          <i class="fa-solid fa-xmark"></i>
        </button>
      </div>

      <div class="popup-body">

        <!-- ==========================================
             SECTION 01: PRODUCT INFORMATION
        =========================================== -->
        <section class="content-section product-section">

          <div class="section-heading">
            <div>
              <h5 class="section-title">
                Product Information
              </h5>

              <p class="section-description">
                Basic item details
              </p>
            </div>
          </div>

          <div class="product-overview">

            <!-- ITEM IMAGE -->
            <div class="item-image-container">
              <img
                :src="imageSrc"
                alt="Item Image"
                class="item-image"
                @error="onImageError"
              />
            </div>

            <!-- ITEM DETAILS -->
            <div class="product-information">

              <div class="product-name">
                {{ itemDetails.item_name }}
              </div>

              <div class="details-grid">

                <div class="detail-item">
                  <span class="detail-label">
                    Item Name
                  </span>

                  <span class="detail-value">
                    {{ itemDetails.item_name }}
                  </span>
                </div>

                <div class="detail-item">
                  <span class="detail-label">
                    Category
                  </span>

                  <span class="detail-value">
                    {{ itemDetails.item_category }}
                  </span>
                </div>

                <div class="detail-item">
                  <span class="detail-label">
                    SAP Code
                  </span>

                  <span class="detail-value">
                    {{ itemDetails.sap_code }}
                  </span>
                </div>

                <div class="detail-item">
                  <span class="detail-label">
                    Is Returned
                  </span>

                  <span class="detail-value">
                    {{
                      itemDetails.is_returned === '1'
                        ? 'Yes'
                        : 'No'
                    }}
                  </span>
                </div>

                <div class="detail-item">
                  <span class="detail-label">
                    Status
                  </span>

                  <span
                    :class="statusClass(itemDetails.status)"
                  >
                    {{
                      itemDetails.status === '1'
                        ? 'ACTIVE'
                        : 'INACTIVE'
                    }}
                  </span>
                </div>

              </div>
            </div>
          </div>

        </section>

        <!-- ==========================================
             SECTION 02: ITEM PRICES
        =========================================== -->
        <section class="content-section prices-section">

          <!-- ITEM PRICES TITLE + DISTRICT SEARCH -->
          <div class="section-heading item-prices-heading">

            <!-- LEFT: SECTION TITLE -->
            <div class="item-prices-title">
              <h5 class="section-title">
                Item Prices
              </h5>

              <p class="section-description">
                District-wise pricing information
              </p>
            </div>

            <!-- RIGHT: DISTRICT SEARCH -->
            <div
              class="district-search"
              role="search"
            >
              <i
                class="fa-solid fa-magnifying-glass district-search-icon"
                aria-hidden="true"
              ></i>

              <input
                id="item-price-district-search"
                v-model="districtSearch"
                type="search"
                class="district-search-input"
                placeholder="Search district..."
                aria-label="Search item prices by district"
                autocomplete="off"
                spellcheck="false"
              />
            </div>

          </div>

          <!-- ======================================
               VARIANT SELECTION
          ======================================= -->
          <div class="variant-section">

            <div class="variant-tabs">
              <button
                v-for="(variant, index) in itemVariants"
                :key="variant.details.id"
                type="button"
                :class="[
                  'price-tabs',
                  {
                    active: selectedVariantIndex === index
                  }
                ]"
                :aria-pressed="selectedVariantIndex === index"
                @click="selectedVariantIndex = index"
              >
                {{ variant.details.variant_name }}
              </button>
            </div>

          </div>

          <!-- ======================================
               FILTERED PRICE TABLE
          ======================================= -->
          <div
            v-if="filteredPrices.length > 0"
            class="price-table"
          >
            <table class="table-container">

              <thead>

                <tr class="first-row">

                  <th rowspan="2" class="district-column">
                    District
                  </th>

                  <th rowspan="2" class="cost-column">
                    Cost (Rs.)
                  </th>

                  <th colspan="2" class="group-column">
                    Distributor Price (Rs.)
                  </th>

                  <th colspan="2" class="group-column">
                    Dealer Price (Rs.)
                  </th>

                  <th colspan="2" class="group-column">
                    Consumer Price (Rs.)
                  </th>

                </tr>

                <tr class="second-row">

                  <th>
                    Without VAT
                  </th>

                  <th>
                    With VAT
                  </th>

                  <th>
                    Without VAT
                  </th>

                  <th>
                    With VAT
                  </th>

                  <th>
                    Without VAT
                  </th>

                  <th>
                    With VAT
                  </th>

                </tr>

              </thead>

              <tbody>

                <tr
                  v-for="(price, i) in filteredPrices"
                  :key="price.district_id ?? i"
                >

                  <td class="district-name">
                    {{ price.district_name }}
                  </td>

                  <td>
                    {{ price.cost ?? '—' }}
                  </td>

                  <td>
                    {{ price.distributor_price ?? '—' }}
                  </td>

                  <td>
                    {{
                      price.distributor_price_with_vat ?? '—'
                    }}
                  </td>

                  <td>
                    {{ price.dealer_price ?? '—' }}
                  </td>

                  <td>
                    {{
                      price.dealer_price_with_vat ?? '—'
                    }}
                  </td>

                  <td>
                    {{ price.consumer_price ?? '—' }}
                  </td>

                  <td>
                    {{
                      price.consumer_price_with_vat ?? '—'
                    }}
                  </td>

                </tr>

              </tbody>

            </table>
          </div>

          <!-- ======================================
               NO PRICES / NO SEARCH RESULTS
          ======================================= -->
          <div
            v-else
            class="no-prices-message"
          >
            <i class="fa-solid fa-circle-info"></i>

            <!-- SEARCH HAS NO MATCHES -->
            <p
              v-if="
                displayedPrices.length > 0 &&
                districtSearch.trim()
              "
            >
              No matching districts found for
              "{{ districtSearch.trim() }}".
            </p>

            <!-- VARIANT HAS NO PRICE RECORDS -->
            <p v-else>
              No price data available for this variant.
            </p>
          </div>

        </section>

      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "ItemDetailsModal",

  props: {
    itemDetails: {
      type: Object,
      required: true
    },

    itemVariants: {
      type: Array,
      required: true
    }
  },

  emits: ["close-modal"],

  data() {
    return {
      selectedVariantIndex: 0,
      imageError: false,

      // District search text.
      // Frontend only; no API calls required.
      districtSearch: ""
    };
  },

  computed: {

  
    // EXISTING VARIANT SELECTION
  
    selectedVariant() {
      return (
        this.itemVariants[this.selectedVariantIndex] || {
          details: {},
          prices: []
        }
      );
    },

  
    // EXISTING BACKEND PRICE DISPLAY
    //
    // Refill -> Stored effective prices
    // Empty  -> Stored effective prices
    // New    -> Backend-calculated prices
    //
    // No price calculation is performed here.
  
    displayedPrices() {
      return this.selectedVariant?.prices || [];
    },

  
    // NEW: DISTRICT SEARCH FILTER
    //
    // - Searches district_name only
    // - Case-insensitive
    // - Supports partial matching
    // - Returns all prices when search is empty
    // - Does not mutate API response data
    // - Automatically works for each variant
    // - No watchers or additional API calls
  
    filteredPrices() {
      const prices = this.displayedPrices;

      const searchText = this.districtSearch
        .trim()
        .toLocaleLowerCase();

      // No search text:
      // Display all district price records.
      if (!searchText) {
        return prices;
      }

      // Filter by district name.
      return prices.filter((price) => {
        const districtName = String(
          price?.district_name ?? ""
        )
          .trim()
          .toLocaleLowerCase();

        return districtName.includes(searchText);
      });
    },

  
    // EXISTING IMAGE HANDLING - UNCHANGED
  
    imageSrc() {
      if (
        this.itemDetails.item_image &&
        !this.imageError
      ) {
        const base64Data =
          this.itemDetails.item_image.trim();

        if (base64Data.startsWith("data:image")) {
          return base64Data;
        }

        return `data:image/png;base64,${base64Data}`;
      } else {
        const text = encodeURIComponent(
          this.itemDetails.item_name ||
          "Item Image Not Found"
        );

        return `https://www.dummyimg.in/placeholder?width=300&height=200&text=${text}&text_color=%23000000&bg_color=%23cccccc&font_size=18&text_position=center&font_style=arial`;
      }
    }
  },

  mounted() {
    document.body.style.overflow = "hidden";
  },

  beforeUnmount() {
    document.body.style.overflow = "";
  },

  methods: {
  
    // CLOSE MODAL
  
    closeModal() {
      this.$emit("close-modal");
    },

  
    // IMAGE ERROR HANDLING
  
    onImageError() {
      this.imageError = true;
    },

  
    // STATUS STYLING
  
    statusClass(status) {
      if (status === "0") {
        return "badge badge-cancel";
      }

      if (status === "1") {
        return "badge badge-active";
      }

      return "badge badge-secondary";
    }
  }
};
</script>

<style scoped>

/* ============================================
   MODAL LAYOUT
============================================ */

.modal {
  position: fixed;
  inset: 0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: stretch;
  z-index: 9999;
  background-color: #ffffff;
}

.popup-content {
  width: 100%;
  height: 100%;
  background-color: #f8fafc;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
}

/* ============================================
   HEADER
============================================ */

.popup-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  padding: 22px 30px;
  background-color: #ffffff;
  border-bottom: 1px solid #e5e7eb;
  flex-shrink: 0;
}

.header-content {
  min-width: 0;
}

.popup-header h3 {
  margin: 0;
  font-size: 21px;
  font-weight: 600;
  color: #111827;
}

.header-description {
  margin: 5px 0 0;
  font-size: 12px;
  color: #6b7280;
}

.close-btn {
  width: 34px;
  height: 34px;
  min-width: 34px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 0;
  cursor: pointer;
  font-size: 16px;
  background-color: #ffffff;
  color: #6b7280;
  display: flex;
  justify-content: center;
  align-items: center;
}

.close-btn:hover {
  background-color: #f3f4f6;
  color: #111827;
}

/* ============================================
   BODY
============================================ */

.popup-body {
  width: 100%;
  max-width: 1600px;
  margin: 0 auto;
  padding: 24px 30px 35px;
  display: flex;
  flex-direction: column;
  gap: 22px;
}

/* ============================================
   SHARED SECTIONS
============================================ */

.content-section {
  background-color: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 24px;
  min-width: 0;
}

.section-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
}

.section-title {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #111827;
}

.section-description {
  margin: 5px 0 0;
  font-size: 12px;
  color: #6b7280;
}

/* ============================================
   PRODUCT INFORMATION
============================================ */

.product-overview {
  display: grid;
  grid-template-columns: 170px minmax(0, 1fr);
  gap: 28px;
  align-items: start;
}

.item-image-container {
  width: 170px;
  height: 150px;
  background-color: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 9px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px;
  overflow: hidden;
}

.item-image {
  display: block;
  width: 100%;
  height: 100%;
  max-width: 100%;
  max-height: 100%;
  border-radius: 6px;
  object-fit: contain;
}

.product-information {
  min-width: 0;
}

.product-name {
  font-size: 17px;
  font-weight: 600;
  color: #111827;
  margin-bottom: 18px;
  overflow-wrap: anywhere;
}

.details-grid {
  display: grid;
  grid-template-columns: repeat(
    3,
    minmax(0, 1fr)
  );
  gap: 20px 24px;
}

.detail-item {
  display: flex;
  flex-direction: column;
  gap: 7px;
  min-width: 0;
}

.detail-label {
  font-size: 12px;
  font-weight: 500;
  color: #6b7280;
}

.detail-value {
  font-size: 13px;
  font-weight: 500;
  color: #1f2937;
  overflow-wrap: anywhere;
}

/* ============================================
   STATUS
============================================ */

.badge-cancel,
.badge-active,
.badge-secondary {
  width: fit-content;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 5px;
  padding: 5px 10px;
  font-size: 11px;
  font-weight: 600;
  line-height: 1.3;
}

.badge-cancel {
  background-color: #fee2e2;
  color: #b91c1c;
}

.badge-active {
  background-color: #dcfce7;
  color: #15803d;
}

.badge-secondary {
  background-color: #e5e7eb;
  color: #4b5563;
}

/* ============================================
   ITEM PRICE SECTION
============================================ */

.prices-section {
  padding-bottom: 26px;
}

/* ============================================
   NEW: ITEM PRICES HEADER + DISTRICT SEARCH
============================================ */

.item-prices-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
}

.item-prices-title {
  min-width: 0;
}

/* Search input container */
.district-search {
  position: relative;
  width: 100%;
  max-width: 300px;
  flex: 0 1 300px;
  margin-left: auto;
}

/* Search icon */
.district-search-icon {
  position: absolute;
  top: 50%;
  left: 13px;
  transform: translateY(-50%);
  font-size: 13px;
  color: #9ca3af;
  pointer-events: none;
}

/* Search input */
.district-search-input {
  display: block;
  width: 100%;
  height: 39px;
  padding: 9px 13px 9px 38px;

  border: 1px solid #d1d5db;
  border-radius: 7px;

  background-color: #ffffff;
  color: #374151;

  font-family: inherit;
  font-size: 12px;
  font-weight: 400;

  box-sizing: border-box;
  outline: none;

  transition:
    border-color 0.15s ease,
    box-shadow 0.15s ease;
}

.district-search-input::placeholder {
  color: #9ca3af;
}

.district-search-input:hover {
  border-color: #9ca3af;
}

.district-search-input:focus {
  border-color: #007bff;
  box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.09);
}

.district-search:focus-within .district-search-icon {
  color: #007bff;
}

/* ============================================
   VARIANT TABS
============================================ */

.variant-section {
  width: 100%;
  margin-bottom: 18px;
  border-bottom: 1px solid #e5e7eb;
}

.variant-tabs {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  padding-bottom: 14px;
}

.price-tabs {
  appearance: none;
  border: 1px solid #d1d5db;
  border-radius: 7px;
  padding: 9px 16px;
  font-size: 12px;
  font-weight: 500;
  background-color: #ffffff;
  color: #374151;
  cursor: pointer;
  white-space: nowrap;
  line-height: 1.4;
}

.price-tabs:hover {
  background-color: #f3f4f6;
  border-color: #9ca3af;
}

.price-tabs.active {
  background-color: #007bff;
  color: #ffffff;
  border-color: #007bff;
}

.price-tabs.active:hover {
  background-color: #0069d9;
  border-color: #0069d9;
}

.price-tabs:focus-visible {
  outline: 2px solid #2563eb;
  outline-offset: 2px;
}

/* ============================================
   PRICE TABLE CONTAINER
============================================ */

.price-table {
  width: 100%;
  overflow-x: auto;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
}

/* ============================================
   TABLE
============================================ */

.table-container {
  width: 100%;
  min-width: 1080px;
  margin: 0;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 12px;
  background-color: #ffffff;
}

.table-container th,
.table-container td {
  padding: 12px 14px;
  border-right: 1px solid #e5e7eb;
  border-bottom: 1px solid #e5e7eb;
  vertical-align: middle;
}

.table-container th:last-child,
.table-container td:last-child {
  border-right: none;
}

.table-container tbody tr:last-child td {
  border-bottom: none;
}

/* ============================================
   TABLE HEADERS
============================================ */

.table-container thead .first-row th {
  background-color: #f1f5f9;
  color: #334155;
  font-size: 12px;
  font-weight: 600;
  text-align: center;
}

.table-container thead .second-row th {
  background-color: #f8fafc;
  color: #64748b;
  font-size: 11px;
  font-weight: 600;
  text-align: center;
}

.district-column {
  min-width: 155px;
}

.cost-column {
  min-width: 100px;
}

.group-column {
  min-width: 210px;
}

/* ============================================
   TABLE BODY
============================================ */

.table-container tbody td {
  background-color: #ffffff;
  color: #374151;
  font-size: 12px;
  font-weight: 400;
  text-align: right;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}

.table-container tbody tr:nth-child(even) td {
  background-color: #f9fafb;
}

.table-container tbody tr:hover td {
  background-color: #f1f5f9;
}

.table-container tbody .district-name {
  text-align: left;
  font-weight: 600;
  color: #1f2937;
}

/* ============================================
   STICKY DISTRICT COLUMN
============================================ */

.table-container th:first-child,
.table-container td:first-child {
  position: sticky;
  left: 0;
  z-index: 1;
}

.table-container thead th:first-child {
  z-index: 2;
}

.table-container tbody tr:nth-child(odd) td:first-child {
  background-color: #ffffff;
}

.table-container tbody tr:nth-child(even) td:first-child {
  background-color: #f9fafb;
}

.table-container tbody tr:hover td:first-child {
  background-color: #f1f5f9;
}

/* ============================================
   NO PRICES / NO SEARCH RESULTS
============================================ */

.no-prices-message {
  min-height: 140px;
  padding: 30px;
  border: 1px dashed #d1d5db;
  border-radius: 8px;
  background-color: #f9fafb;
  color: #6b7280;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  text-align: center;
}

.no-prices-message i {
  font-size: 19px;
  color: #9ca3af;
}

.no-prices-message p {
  margin: 0;
  font-size: 13px;
}

/* ============================================
   TABLET RESPONSIVE
============================================ */

@media (max-width: 1024px) {
  .popup-header {
    padding: 20px 22px;
  }

  .popup-body {
    padding: 20px 22px 28px;
  }

  .content-section {
    padding: 20px;
  }

  .details-grid {
    grid-template-columns: repeat(
      2,
      minmax(0, 1fr)
    );
  }

  .product-overview {
    grid-template-columns: 150px minmax(0, 1fr);
    gap: 20px;
  }

  .item-image-container {
    width: 150px;
    height: 145px;
  }
}

/* ============================================
   MOBILE RESPONSIVE
============================================ */

@media (max-width: 640px) {
  .popup-header {
    padding: 16px;
  }

  .popup-header h3 {
    font-size: 19px;
  }

  .popup-body {
    padding: 16px;
    gap: 16px;
  }

  .content-section {
    padding: 16px;
    border-radius: 8px;
  }

  .product-overview {
    grid-template-columns: 1fr;
    gap: 16px;
  }

  .item-image-container {
    width: 100%;
    max-width: 180px;
    height: 140px;
  }

  .product-name {
    font-size: 15px;
    margin-bottom: 16px;
  }

  .details-grid {
    grid-template-columns: repeat(
      2,
      minmax(0, 1fr)
    );
    gap: 16px;
  }

  .section-title {
    font-size: 15px;
  }

  /* Search moves below the title on smaller screens */
  .item-prices-heading {
    align-items: stretch;
    flex-direction: column;
    gap: 12px;
  }

  .district-search {
    max-width: 100%;
    width: 100%;
    flex: 0 1 auto;
    margin-left: 0;
  }

  .district-search-input {
    height: 40px;
    font-size: 13px;
  }

  .variant-tabs {
    gap: 6px;
  }

  .price-tabs {
    font-size: 11px;
    padding: 8px 12px;
  }

  .table-container {
    min-width: 1000px;
  }
}

@media (max-width: 380px) {
  .details-grid {
    grid-template-columns: 1fr;
  }
}

</style>